import { twMerge } from '@/lib/classname/twMerge'
import { cva } from 'class-variance-authority'
import type { ElementType, ReactNode } from 'react'
import { ListItem } from './_components/ListItem'

export type ListSize = 'sm' | 'md'

export type ListType = 'bullet' | 'ordered'
const listTypesComponentMap: Record<ListType, ElementType> = {
  bullet: 'ul',
  ordered: 'ol',
}

type ListBaseProps = UiComponentProps & {
  size?: ListSize
  bold?: boolean
  type?: ListType
  withCmsSpacing?: boolean
}

type ListCompoundProps = ListBaseProps & {
  children: ReactNode
  items?: never
}

type ListItemsProps = ListBaseProps & {
  items: ReadonlyArray<string>
  children?: never
}

type ListProps = ListCompoundProps | ListItemsProps

const listStyles = cva('group', {
  variants: {
    type: {
      bullet: 'list-disc',
      ordered: 'list-decimal marker:font-bold',
    },
    size: {
      md: '',
      sm: 'text-sm',
    },
    bold: {
      true: 'font-bold',
    },
    withCmsSpacing: {
      true: 'ml-3',
    },
  },
  compoundVariants: [
    {
      type: 'bullet',
      size: 'md',
      className: 'space-y-0 marker:text-2xl',
    },
    {
      type: 'ordered',
      size: 'md',
      className: 'space-y-0.5',
    },
  ],
})

export function List({
  className,
  'data-testid': dataTestId,
  items,
  size = 'md',
  bold = false,
  children,
  type = 'bullet',
  withCmsSpacing = false,
  ...props
}: ListProps) {
  const Component = listTypesComponentMap[type]

  return (
    <Component
      {...props}
      className={twMerge(listStyles({ type, size, bold, withCmsSpacing }), className)}
      data-testid={dataTestId}
      data-type={type}
    >
      {items ? items.map((item) => <ListItem key={item}>{item}</ListItem>) : children}
    </Component>
  )
}

List.Item = ListItem
